"use client";

import { c as _c } from "react/compiler-runtime";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { IconAlertCircleFilled, IconCircleCheckFilled } from "@tabler/icons-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmailFormMessages } from "@/constants/EmailFormMessages";
import { identifyLeadFormCompleted, trackLeadFormCompleted, trackLeadFormError } from "@/lib/analytics/segment";
import { cn } from "@/lib/utils";
import { isEmailValid } from "@/lib/validation";
import { useServicesStore } from "@/stores/useServicesStore";
export interface Props {
  heading?: string;
  description?: string;
  consentCopy: Parameters<typeof documentToReactComponents>[0];
  successCopy: string;
  buttonText: string;
  source?: string;
  errorVariant?: "inline" | "badge";
  /** Set when rendered inside the promo modal: both Lead Form events then carry the popup. */
  popupId?: string;
  /** When the popup opened, so a submission reports how long it took. */
  shownAt?: number;
  /** Lightens the success message text so it stays readable on dark surfaces (footer). */
  onDarkSurface?: boolean;
}
type Status = "idle" | "submitting" | "success" | "error";
function FormIntro(t0) {
  const $ = _c(7);
  const {
    heading,
    description
  } = t0;
  if (!heading && !description) {
    return null;
  }
  let t1;
  if ($[0] !== heading) {
    t1 = heading && <p className="font-semibold">{heading}</p>;
    $[0] = heading;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = description && <p className="text-sm text-muted-foreground">{description}</p>;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-col gap-1.5">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function EmailForm({
  heading,
  description,
  consentCopy,
  successCopy,
  buttonText,
  source,
  errorVariant = "inline",
  onDarkSurface = false,
  popupId,
  shownAt
}: Props) {
  const eventClientPublicFetch = useServicesStore(state => state.eventClientPublicFetch);
  const [email, setEmail] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Props present only inside the promo modal, which passes both from the same open.
  function popupContext() {
    if (popupId === undefined || shownAt === undefined) return {};
    return {
      popupId,
      timeVisibleMs: Date.now() - shownAt
    };
  }
  function handleFail(message: string, errorType: "validation" | "network") {
    setStatus("error");
    setErrorMessage(message);
    trackLeadFormError({
      errorType,
      ...(source && {
        source
      }),
      ...popupContext()
    });
  }
  async function handleSubmit(e: React.SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!consentChecked) {
      handleFail(EmailFormMessages.consentError, "validation");
      return;
    }
    if (!isEmailValid(email)) {
      handleFail(EmailFormMessages.emailError, "validation");
      return;
    }
    if (!eventClientPublicFetch) {
      handleFail(EmailFormMessages.defaultError, "network");
      return;
    }
    setStatus("submitting");
    setErrorMessage(null);
    try {
      const customer = await eventClientPublicFetch.subscribeWebsite({
        email,
        ...(source && {
          source
        })
      });
      identifyLeadFormCompleted({
        ...customer,
        source
      });
      trackLeadFormCompleted({
        ...customer,
        source,
        ...popupContext()
      });
      setStatus("success");
    } catch {
      handleFail(EmailFormMessages.defaultError, "network");
    }
  }
  const isSuccess = status === "success";
  const isSubmitting = status === "submitting";
  const hasEmailError = errorMessage === EmailFormMessages.emailError;
  const hasConsentError = errorMessage === EmailFormMessages.consentError;
  if (isSuccess) {
    return <output aria-live="polite" className="flex flex-col items-center gap-4 py-10 text-center animate-in zoom-in-75 fade-in duration-300">
        <IconCircleCheckFilled size={64} className="shrink-0 text-green-600" aria-hidden />
        <p className={cn("font-medium text-pretty", onDarkSurface ? "text-gray-200" : "text-gray-700")}>
          {successCopy}
        </p>
      </output>;
  }
  return <form onSubmit={handleSubmit} noValidate className="w-full space-y-6">
      <FormIntro heading={heading} description={description} />

      <div>
        <Label htmlFor="email-form-input" className="text-sm font-medium pb-3">
          Email address
        </Label>
        <Input id="email-form-input" className="bg-background text-foreground" type="email" placeholder="youremail@domain.com" value={email} onChange={e_0 => {
        setEmail(e_0.target.value);
        if (hasEmailError) setErrorMessage(null);
      }} aria-invalid={hasEmailError ? true : undefined} aria-describedby={hasEmailError ? "email-form-error" : undefined} />

        <label className="flex cursor-pointer items-start gap-2 pt-4">
          <input type="checkbox" id="email-form-consent" checked={consentChecked} onChange={e_1 => {
          setConsentChecked(e_1.target.checked);
          if (hasConsentError) setErrorMessage(null);
        }} onKeyDown={e_2 => {
          if (e_2.key === "Enter") e_2.preventDefault();
        }} aria-invalid={hasConsentError ? true : undefined} aria-describedby={hasConsentError ? "email-form-error" : undefined} className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-primary disabled:opacity-50" />
          <span className="text-sm leading-relaxed font-normal [&_a]:underline">
            {documentToReactComponents(consentCopy)}
          </span>
        </label>
      </div>

      <Button type="submit" disabled={!email || isSubmitting} className="w-full mb-2.5">
        {buttonText}
      </Button>

      <div className="min-h-6 flex items-center justify-center">
        {errorMessage && (errorVariant === "badge" ? <span id="email-form-error" role="alert" className="inline-flex items-center justify-center gap-1 rounded-md border border-transparent bg-destructive px-2 py-0.5 text-sm text-white">
              {errorMessage}
              <IconAlertCircleFilled size={16} />
            </span> : <p id="email-form-error" role="alert" className="text-sm text-destructive text-center">
              {errorMessage}
            </p>)}
      </div>
    </form>;
}